// src/helpers/request.ts
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getAccessToken } from 'auth/tokenAccessor';
import { userManager } from 'auth/AuthManager';
import {
	AUTH_ERROR,
	NO_ERROR_INFO,
	SERVER_ERROR,
	TECHNICAL_ERROR,
} from 'utils/constants/errors';
import { RequestMethodTypes } from '../models';
import { filterFalsyDataValues } from './filterFalsyDataValues';

export interface IRequestParams {
	url: string;
	method?: RequestMethodTypes;
	headers?: any;
	payload?: any;
	data?: any;
	file?: Record<string, any>;
	params?: any;
}

export const request = async (conditions: IRequestParams) => {
	const {
		url,
		method = 'GET',
		headers: callerHeaders,
		data,
		params,
		file,
	} = conditions;

	// Read token from in-memory accessor and inject as Bearer header
	const token = await getAccessToken();

	const defaultHeaders: Record<string, string> = {
		'content-type': 'application/json',
		...(token ? { Authorization: `Bearer ${token}` } : {}),
	};

	const queryString: string = formQueryString(params);
	const optionsData = file || (data && filterFalsyDataValues(data));

	const options: AxiosRequestConfig = {
		url: `${url}${queryString}`,
		method,
		headers: { ...defaultHeaders, ...callerHeaders }, // Change 2: merge headers
		data: optionsData,
		timeout: 525000,
	};

	return axios(options)
		.then((response) => {
			if (response.data.errorMessage) {
				throw new Error(response.data.errorMessage);
			}
			return response;
		})
		.catch(async (error: AxiosError) => {
			if (!error.response) {
				throw new Error(TECHNICAL_ERROR);
			}
			if (error.response.status === 500) {
				throw new Error(SERVER_ERROR);
			}
			// Attempt silent refresh before giving up on 401
			if (error.response.status === 401) {
				// Check if we have a user at all — if not, no point trying silent refresh
				const currentUser = await userManager.getUser();

				if (currentUser && !currentUser.expired) {
					// We have a valid user but still got 401 — strange, but throw rather than redirect
					throw new Error(AUTH_ERROR);
				}

				if (currentUser && currentUser.expired) {
					// We have an expired user — silent refresh via refresh_token grant is appropriate
					try {
						await userManager.signinSilent();
						// After signinSilent resolves, oidc-client-ts has stored the new token.
						// Read it back through the same accessor used everywhere else.
						const newToken = await getAccessToken();
						if (newToken) {
							// eslint-disable-next-line
							return axios({
								...options,
								headers: {
									...options.headers,
									Authorization: `Bearer ${newToken}`,
								},
							});
						}
					} catch {
						// Silent refresh failed — fall through to redirect
					}
				}

				// No user, or refresh failed — redirect to Cognito for fresh login
				await userManager.signinRedirect();
				throw new Error(AUTH_ERROR);
			}

			throw error.response.data || new Error(NO_ERROR_INFO);
		});
};

function formQueryString(params: any = {}): string {
	const paramsToParse = { ...params };
	const filteredQueryDict: { [k: string]: any } = {};
	Object.keys(paramsToParse).forEach((key) => {
		const value = params[key];
		if (typeof value !== 'undefined' && value !== '' && value !== null) {
			filteredQueryDict[key] = value;
		}
	});
	const chainedQueryString = Object.keys(filteredQueryDict)
		.map((param) => `${[param]}=${filteredQueryDict[param]}`)
		.join('&');
	return chainedQueryString ? `?${chainedQueryString}` : '';
}
